import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import axios from 'axios';
import Picture from './Picture';

const LOGO_URL = '/images/glossop_rt_logo.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_NAV_LINKS = [
  { label: 'Home', path: '/' },
  {
    label: 'About Us',
    path: '/about',
    children: [
      { label: 'About Us', path: '/about' },
      { label: 'Charities We Support', path: '/charities' },
      { label: 'Socials', path: '/socials' },
      { label: 'Social Calendar 2026', path: '/social-calendar' },
    ],
  },
  {
    label: 'Membership',
    path: '/membership',
    children: [
      { label: 'Join Us', path: '/membership' },
      { label: 'Member Benefits', path: '/member-benefits' },
    ],
  },
  { label: 'News', path: '/news' },
  // Donation Request slot — inserted dynamically below when the back-end
  // toggle is on, sitting between News and Events as requested.
  {
    label: 'Events',
    path: '/events',
    children: [
      { label: 'Events', path: '/events' },
      { label: 'Glossop Heritage Weekend', path: '/heritage-programme' },
      { label: 'GlossFest', path: '/glossfest' },
      { label: 'Bonfire Night', path: '/bonfire' },
      { label: 'Volunteering', path: '/volunteer' },
      { label: 'Our Supporters', path: '/supporters' },
    ],
  },
  // Round Table Bar page temporarily hidden from nav/sitemap (route still works
  // for anyone with a bookmark or printed QR). Reinstate when the bar schedule
  // is back on.
  { label: 'Contact', path: '/contact' },
];

function useDonationsOpen() {
  // Polls `/api/donations/status` every 60s (cheap, public endpoint) so admins
  // can flip the toggle and see the nav update without a hard reload.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/status`);
        if (!cancelled) setOpen(Boolean(data?.donations_open));
      } catch {
        if (!cancelled) setOpen(false);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);
  return open;
}

function NavLink({ link, isActive }) {
  return (
    <Link
      to={link.path}
      data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`px-4 py-2 text-sm font-semibold tracking-wide transition-colors
        ${isActive(link.path) ? 'text-[#F0A500]' : 'text-[#0A0A0A] hover:text-[#F0A500]'}`}
    >
      {link.label}
    </Link>
  );
}

function DropdownNav({ link, isActive, dropdownOpen, setDropdownOpen }) {
  return (
    <div
      className="relative"
      onMouseEnter={() => setDropdownOpen(link.label)}
      onMouseLeave={() => setDropdownOpen(null)}
    >
      <button
        data-testid={`nav-${link.label.toLowerCase()}-dropdown`}
        className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold tracking-wide transition-colors
          ${link.children.some((c) => isActive(c.path))
            ? 'text-[#F0A500]'
            : 'text-[#0A0A0A] hover:text-[#F0A500]'
          }`}
      >
        {link.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen === link.label ? 'rotate-180' : ''}`} />
      </button>
      {dropdownOpen === link.label && (
        <div className="absolute top-full left-0 pt-0 w-56">
          <div className="bg-white border border-black/10 shadow-lg py-1">
            {link.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                data-testid={`nav-link-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`block px-4 py-2.5 text-sm font-medium transition-colors
                  ${isActive(child.path)
                    ? 'text-[#F0A500] bg-[#F0A500]/5'
                    : 'text-[#0A0A0A] hover:text-[#F0A500] hover:bg-[#F0A500]/5'
                  }`}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileMenu({ isActive, navLinks }) {
  return (
    <div className="md:hidden mobile-menu-overlay border-t border-black/10">
      <div className="px-4 py-4 space-y-1">
        {navLinks.map((link) =>
          link.children ? (
            <div key={link.label}>
              <div className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#52525B]">
                {link.label}
              </div>
              {link.children.map((child) => (
                <Link
                  key={child.path}
                  to={child.path}
                  data-testid={`mobile-nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block px-6 py-2.5 text-sm font-medium transition-colors
                    ${isActive(child.path) ? 'text-[#F0A500]' : 'text-[#0A0A0A] hover:text-[#F0A500]'}`}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          ) : (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`block px-3 py-2.5 text-sm font-semibold transition-colors
                ${isActive(link.path) ? 'text-[#F0A500]' : 'text-[#0A0A0A] hover:text-[#F0A500]'}`}
            >
              {link.label}
            </Link>
          )
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const location = useLocation();
  const donationsOpen = useDonationsOpen();

  // Insert the Donation Request link between News and Events when the toggle
  // is on. Keeping the source array immutable so React re-renders cleanly.
  const navLinks = useMemo(() => {
    if (!donationsOpen) return BASE_NAV_LINKS;
    const eventsIdx = BASE_NAV_LINKS.findIndex((l) => l.label === 'Events');
    if (eventsIdx === -1) return BASE_NAV_LINKS;
    const donationItem = { label: 'Donation Request', path: '/donation-request' };
    return [
      ...BASE_NAV_LINKS.slice(0, eventsIdx),
      donationItem,
      ...BASE_NAV_LINKS.slice(eventsIdx),
    ];
  }, [donationsOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(null);
  }, [location]);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  return (
    <nav
      data-testid="main-navbar"
      className={`glass-nav fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'scrolled' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24">
          <Link to="/" data-testid="nav-logo-link" className="flex-shrink-0">
            <Picture
              src={LOGO_URL}
              alt="Glossop & District Round Table 916"
              className="h-14 sm:h-20 w-auto object-contain"
              loading="eager"
            />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <DropdownNav
                  key={link.label}
                  link={link}
                  isActive={isActive}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              ) : (
                <NavLink key={link.path} link={link} isActive={isActive} />
              )
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileMenu isActive={isActive} navLinks={navLinks} />}
    </nav>
  );
}
